/**
 * /field/wt/jobs/:id — one of the hand's jobs (GET
 * /well-testing/field/jobs/:id, fieldiq-api#1245). SNAKE_CASE.
 *
 * CREW ONLY: the API answers 403 not_on_crew to anyone else, whatever their
 * role, and this screen says so in a sentence rather than as a load failure.
 * Rent-ins come without vendor rates (the field surface never sends them).
 * The actions are "Enter reading" (WT-3, WtFieldReadingScreen) and today's
 * day and night logs, on the tenant's today from `meta.today`.
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ClipboardList, Gauge } from 'lucide-react';
import { useWtFieldJob } from '../../../hooks/useWellTesting';
import { queryFailureError } from '../../../lib/load-failure';
import { wtDay, wtErrorCode, wtJobLoggable, wtJobStatusSkin, wtRefusal, wtRentStatusSkin } from '../../../lib/well-testing';
import {
  PT_FIELD_PRIMARY,
  PT_FIELD_SECONDARY,
  PtFieldBadge,
  PtFieldCard,
  PtFieldPage,
  PtFieldQueryState,
  PtFieldSectionTitle,
  PtRefusal,
} from '../../pressure-testing/field/PtFieldBits';
import { WT_SOUR_SKIN } from '../WtBits';

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-b border-fieldiq-border px-4 py-2.5 last:border-b-0">
      <span className="text-sm text-fieldiq-muted">{label}</span>
      <span className="text-right text-sm font-semibold text-white">{value}</span>
    </div>
  );
}

/** A refusal that is the API's "not on this crew": a sentence, not a load failure. */
export function wtNotOnCrewMessage(query: { error?: unknown; failureReason?: unknown }): string | null {
  const err = queryFailureError(query);
  return wtErrorCode(err) === 'not_on_crew' ? wtRefusal(err, '') : null;
}

export default function WtFieldJobScreen() {
  const { id } = useParams<{ id: string }>();
  const query = useWtFieldJob(id);
  const job = query.data?.data ?? null;
  const today = query.data?.meta?.today ?? null;
  const refused = wtNotOnCrewMessage(query);
  const loggable = wtJobLoggable(job?.status);

  return (
    <PtFieldPage title={job?.job_number || 'Job'} backTo="/field/wt" testId="wt-field-job">
      {refused ? (
        <PtRefusal message={refused} testId="wt-field-not-on-crew" />
      ) : (
        <PtFieldQueryState query={query} subject="this job">
          {!job ? (
            <PtRefusal message="That job no longer exists. It may have been deleted." />
          ) : (
            <div className="space-y-4">
              <PtFieldCard>
                <div className="flex items-center justify-between gap-2 border-b border-fieldiq-border px-4 py-3">
                  <span className="text-base font-bold text-white">{job.customer_name || '—'}</span>
                  <span className="flex gap-2">
                    {job.well_sour && <PtFieldBadge skin={WT_SOUR_SKIN} />}
                    <PtFieldBadge skin={wtJobStatusSkin(job.status)} />
                  </span>
                </div>
                <Row label="Well" value={job.well_name || '—'} />
                <Row label="UWI / API" value={job.well_uwi || job.well_api_number || '—'} />
                <Row label="Kind" value={job.job_kind_label || '—'} />
                <Row label="Supervisor" value={job.supervisor_name || '—'} />
                <Row label="Planned" value={`${wtDay(job.planned_start_on)} → ${wtDay(job.planned_end_on)}`} />
                {job.scope && <p className="px-4 py-3 text-sm text-fieldiq-text">{job.scope}</p>}
              </PtFieldCard>

              {loggable && today ? (
                <div className="space-y-2" data-testid="wt-field-today-logs">
                  <Link to={`/field/wt/jobs/${job.id}/reading`} className={PT_FIELD_PRIMARY} data-testid="wt-field-enter-reading">
                    <Gauge className="size-5" aria-hidden="true" />
                    Enter reading
                  </Link>
                  {(['day', 'night'] as const).map((shift) => {
                    const written = job.today_logs?.[shift];
                    return (
                      <Link
                        key={shift}
                        to={`/field/wt/jobs/${job.id}/log/${today}/${shift}`}
                        className={written ? PT_FIELD_SECONDARY : PT_FIELD_PRIMARY}
                        data-testid={`wt-field-log-${shift}`}
                      >
                        <ClipboardList className="size-5" aria-hidden="true" />
                        {written ? `Today's ${shift} log (${wtDay(today)})` : `Write today's ${shift} log (${wtDay(today)})`}
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <PtRefusal message={job.status === 'draft' ? 'This job is still a draft. The office schedules it before shift logs can be written.' : 'This job is closed. Its shift logs can no longer be written.'} />
              )}

              <section>
                <PtFieldSectionTitle>Crew · {job.crew?.length ?? 0}</PtFieldSectionTitle>
                <PtFieldCard>
                  {(job.crew ?? []).length === 0 ? (
                    <p className="px-4 py-3 text-sm text-fieldiq-muted">No crew yet.</p>
                  ) : (
                    job.crew.map((c) => <Row key={c.id} label={c.user_name || '—'} value={[c.crew_role_label, c.shift_label].filter(Boolean).join(' · ') || '—'} />)
                  )}
                </PtFieldCard>
              </section>

              <section>
                <PtFieldSectionTitle>Equipment · {job.equipment?.length ?? 0}</PtFieldSectionTitle>
                <PtFieldCard>
                  {(job.equipment ?? []).length === 0 ? (
                    <p className="px-4 py-3 text-sm text-fieldiq-muted">No equipment on this job.</p>
                  ) : (
                    job.equipment.map((e) => (
                      <Row key={e.id} label={e.equipment_name || e.unit_number || '—'} value={[e.component_kind_label, e.package_name].filter(Boolean).join(' · ') || '—'} />
                    ))
                  )}
                </PtFieldCard>
              </section>

              {(job.rent_ins ?? []).length > 0 && (
                <section>
                  <PtFieldSectionTitle>Rent-ins · {job.rent_ins.length}</PtFieldSectionTitle>
                  <PtFieldCard>
                    {job.rent_ins.map((r) => (
                      <div key={r.id} className="flex items-center justify-between gap-3 border-b border-fieldiq-border px-4 py-2.5 last:border-b-0">
                        <span className="min-w-0 text-sm">
                          <span className="block truncate font-semibold text-white">{r.description || '—'}</span>
                          <span className="block truncate text-xs text-fieldiq-muted">
                            {r.vendor_name} · {wtDay(r.on_rent_on)} → {wtDay(r.off_rent_on)}
                          </span>
                        </span>
                        <PtFieldBadge skin={wtRentStatusSkin(r.rent_status)} />
                      </div>
                    ))}
                  </PtFieldCard>
                </section>
              )}
            </div>
          )}
        </PtFieldQueryState>
      )}
    </PtFieldPage>
  );
}
